import { Link } from "react-router-dom";
import { Shield, Users, Headphones, Server, Lock, BarChart3 } from "lucide-react";

const features = [
  { icon: Shield, title: "Advanced security", text: "SOC 2 Type II, SSO/SAML, role-based access control, audit logs, and encryption at rest." },
  { icon: Users, title: "Team management", text: "Unlimited seats, granular permissions, and centralized billing for your entire organization." },
  { icon: Headphones, title: "Dedicated support", text: "24/7 phone and email support with a named account manager and guaranteed response times." },
  { icon: Server, title: "Dedicated infrastructure", text: "Isolated compute, private networking, and custom deployment regions for compliance." },
  { icon: Lock, title: "Compliance", text: "GDPR, HIPAA, and SOC 2 compliance built in. We handle the paperwork so you don't have to." },
  { icon: BarChart3, title: "Custom SLAs", text: "99.99% uptime guarantee with financial credits. Tailored agreements for your business needs." },
];

const logos = ["Acme Corp", "Globex", "Initech", "Massive Dynamic", "Hooli", "Pied Piper"];

const EnterprisePage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="enterprise-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-[12px] font-bold text-[#ff9a4d] tracking-wide uppercase mb-4">Enterprise</p>
            <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124] leading-tight" data-testid="enterprise-title">
              Cloud infrastructure your security team will love
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5f6368] leading-relaxed max-w-2xl">
              Everything in Eamico Pro, plus advanced security, compliance, dedicated support, and custom infrastructure for organizations with complex requirements.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="#contact-form" className="inline-flex items-center px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm">
                Contact us
              </Link>
              <Link to="/pricing" className="inline-flex items-center px-6 py-3 border border-[#dadce0] hover:bg-white text-[14px] font-medium text-[#ff9a4d] rounded-md transition-colors">
                View pricing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Trusted by */}
      <div className="border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-10">
          <p className="text-[12px] font-bold text-[#9aa0a6] tracking-wide uppercase text-center mb-8">Trusted by industry leaders</p>
          <div className="flex flex-wrap justify-center gap-8 lg:gap-16" data-testid="enterprise-logos">
            {logos.map((name) => (
              <span key={name} className="text-[15px] font-medium text-[#dadce0]">{name}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-12">Enterprise-grade features</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="enterprise-features">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="p-8 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all" data-testid={`enterprise-feature-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#fff4eb] flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#ff9a4d]" />
                </div>
                <h3 className="text-[16px] font-medium text-[#202124] mb-2">{f.title}</h3>
                <p className="text-[14px] text-[#5f6368] leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Contact form */}
      <div id="contact-form" className="bg-[#f8f9fa] border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-xl mx-auto">
            <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] text-center mb-4">Get in touch</h2>
            <p className="text-[15px] text-[#5f6368] text-center mb-10">Tell us about your needs and we'll get back to you within 24 hours.</p>
            <form className="space-y-5" data-testid="enterprise-form" onSubmit={(e) => e.preventDefault()}>
              <div className="grid sm:grid-cols-2 gap-4">
                <input type="text" placeholder="First name" className="w-full px-4 py-3 text-[14px] rounded-lg border border-[#dadce0] focus:border-[#ff9a4d] focus:ring-1 focus:ring-[#ff9a4d] outline-none transition-colors" data-testid="enterprise-first-name" />
                <input type="text" placeholder="Last name" className="w-full px-4 py-3 text-[14px] rounded-lg border border-[#dadce0] focus:border-[#ff9a4d] focus:ring-1 focus:ring-[#ff9a4d] outline-none transition-colors" data-testid="enterprise-last-name" />
              </div>
              <input type="email" placeholder="Work email" className="w-full px-4 py-3 text-[14px] rounded-lg border border-[#dadce0] focus:border-[#ff9a4d] focus:ring-1 focus:ring-[#ff9a4d] outline-none transition-colors" data-testid="enterprise-email" />
              <input type="text" placeholder="Company" className="w-full px-4 py-3 text-[14px] rounded-lg border border-[#dadce0] focus:border-[#ff9a4d] focus:ring-1 focus:ring-[#ff9a4d] outline-none transition-colors" data-testid="enterprise-company" />
              <textarea placeholder="Tell us about your needs" rows={4} className="w-full px-4 py-3 text-[14px] rounded-lg border border-[#dadce0] focus:border-[#ff9a4d] focus:ring-1 focus:ring-[#ff9a4d] outline-none transition-colors resize-none" data-testid="enterprise-message" />
              <button type="submit" className="w-full px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm" data-testid="enterprise-submit">
                Submit
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterprisePage;
